<script setup>
// NOTICE: KAPTION-INTEGRATION
// Two-factor authentication over WhatsApp. Enroll flow: confirm a phone
// (prefilled from the profile) → verification code arrives on WhatsApp →
// verify → enabled. Codes live only in Redis server-side. Layout follows
// components-next/Campaigns/KAPTION_PLATFORM_STYLE.md tokens.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import kaptionWhatsapp2fa from 'dashboard/api/kaptionWhatsapp2fa';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const loading = ref(true);
const status = ref({ enrolled: false, verified: false, phone: null });
const phoneInput = ref('');
const codeInput = ref('');
const codeSent = ref(false);
const busy = ref(false);
const cooldownLeft = ref(0);
let cooldownTimer = null;

const showDisable = ref(false);
const disablePassword = ref('');

const errorMessage = e =>
  e?.response?.data?.error || t('KAPTION_WA2FA.GENERIC_ERROR');

const sendLabel = computed(() =>
  cooldownLeft.value > 0
    ? t('KAPTION_WA2FA.ENROLL.RESEND_IN', { seconds: cooldownLeft.value })
    : t('KAPTION_WA2FA.ENROLL.SEND_CODE')
);

const startCooldown = () => {
  cooldownLeft.value = 60;
  clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    cooldownLeft.value -= 1;
    if (cooldownLeft.value <= 0) clearInterval(cooldownTimer);
  }, 1000);
};

const loadStatus = async () => {
  const { data } = await kaptionWhatsapp2fa.getStatus();
  status.value = data;
  if (!phoneInput.value) phoneInput.value = data.prefill_phone || '';
};

onMounted(async () => {
  if (!isKaptionFlagEnabled('KAPTION_WHATSAPP_2FA')) {
    router.push({
      name: 'profile_settings_index',
      params: { accountId: route.params.accountId },
    });
    return;
  }
  try {
    await loadStatus();
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => clearInterval(cooldownTimer));

const sendCode = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionWhatsapp2fa.sendCode(phoneInput.value);
    phoneInput.value = data.phone;
    codeSent.value = true;
    startCooldown();
    useAlert(t('KAPTION_WA2FA.ENROLL.CODE_SENT'));
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

const verifyCode = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionWhatsapp2fa.verify(codeInput.value);
    status.value = data;
    codeInput.value = '';
    codeSent.value = false;
    useAlert(t('KAPTION_WA2FA.ENROLL.VERIFIED'));
    // Refresh currentUser so the banner reacts immediately.
    store.dispatch('validityCheck');
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

const disable2fa = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionWhatsapp2fa.disable(disablePassword.value);
    status.value = data;
    showDisable.value = false;
    disablePassword.value = '';
    useAlert(t('KAPTION_WA2FA.DISABLE.DONE'));
    store.dispatch('validityCheck');
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

const goBack = () =>
  router.push({
    name: 'profile_settings_index',
    params: { accountId: route.params.accountId },
  });
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl gap-5 p-6 overflow-y-auto">
    <div class="flex items-start gap-3">
      <Button
        type="button"
        ghost
        icon="i-lucide-arrow-left"
        class="flex-shrink-0 mt-0.5"
        @click="goBack"
      />
      <div class="min-w-0">
        <h1 class="text-lg font-semibold text-n-slate-12">
          {{ $t('KAPTION_WA2FA.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('KAPTION_WA2FA.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('KAPTION_WA2FA.LOADING') }}
    </div>

    <template v-else>
      <!-- Enabled state -->
      <div
        v-if="status.verified"
        class="bg-n-background rounded-xl border border-n-weak p-5"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3 min-w-0">
            <span
              class="flex items-center justify-center size-9 rounded-full bg-n-teal-3 flex-shrink-0"
            >
              <Icon
                icon="i-lucide-shield-check"
                class="size-4 text-n-teal-11"
              />
            </span>
            <div class="min-w-0">
              <h5 class="text-sm font-semibold text-n-slate-12">
                {{ $t('KAPTION_WA2FA.STATUS.VERIFIED_TITLE') }}
              </h5>
              <p class="text-sm text-n-slate-11 truncate">
                {{
                  $t('KAPTION_WA2FA.STATUS.VERIFIED_DESC', {
                    phone: status.phone,
                  })
                }}
              </p>
            </div>
          </div>
          <Button
            type="button"
            faded
            ruby
            :label="$t('KAPTION_WA2FA.DISABLE.BUTTON')"
            class="flex-shrink-0"
            @click="showDisable = !showDisable"
          />
        </div>
        <div
          v-if="showDisable"
          class="mt-4 pt-4 border-t border-n-weak flex flex-col gap-2"
        >
          <label class="text-sm text-n-slate-11" for="wa2fa-password">
            {{ $t('KAPTION_WA2FA.DISABLE.PASSWORD_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              id="wa2fa-password"
              v-model="disablePassword"
              type="password"
              autocomplete="current-password"
              class="flex-1 h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand outline-none"
            />
            <Button
              type="button"
              ruby
              :label="$t('KAPTION_WA2FA.DISABLE.CONFIRM')"
              :disabled="busy || !disablePassword"
              class="flex-shrink-0"
              @click="disable2fa"
            />
          </div>
          <p class="text-xs text-n-ruby-11">
            {{ $t('KAPTION_WA2FA.DISABLE.WARNING') }}
          </p>
        </div>
      </div>

      <!-- Enroll state -->
      <div v-else class="bg-n-background rounded-xl border border-n-weak p-5">
        <h5 class="text-sm font-semibold text-n-slate-12">
          {{ $t('KAPTION_WA2FA.ENROLL.TITLE') }}
        </h5>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('KAPTION_WA2FA.ENROLL.DESCRIPTION') }}
        </p>

        <div class="mt-4 flex flex-col gap-2">
          <label class="text-sm text-n-slate-11" for="wa2fa-phone">
            {{ $t('KAPTION_WA2FA.ENROLL.PHONE_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              id="wa2fa-phone"
              v-model="phoneInput"
              type="tel"
              :placeholder="$t('KAPTION_WA2FA.ENROLL.PHONE_PLACEHOLDER')"
              class="flex-1 h-10 px-3 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand outline-none"
            />
            <Button
              type="button"
              :label="sendLabel"
              :disabled="busy || cooldownLeft > 0 || !phoneInput"
              class="flex-shrink-0"
              @click="sendCode"
            />
          </div>
        </div>

        <div v-if="codeSent" class="mt-4 flex flex-col gap-2">
          <label class="text-sm text-n-slate-11" for="wa2fa-code">
            {{ $t('KAPTION_WA2FA.ENROLL.CODE_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <input
              id="wa2fa-code"
              v-model="codeInput"
              type="text"
              inputmode="numeric"
              maxlength="6"
              :placeholder="$t('KAPTION_WA2FA.ENROLL.CODE_PLACEHOLDER')"
              class="flex-1 h-10 px-3 text-sm font-mono tracking-widest rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand outline-none"
            />
            <Button
              type="button"
              teal
              :label="$t('KAPTION_WA2FA.ENROLL.VERIFY')"
              :disabled="busy || codeInput.length !== 6"
              class="flex-shrink-0"
              @click="verifyCode"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
